import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationError, NavigationStart, Router } from '@angular/router';

/**
 * Tells a navigation that failed and still matters apart from one the app has already
 * moved past.
 *
 * It exists for {@link LazyChunkErrorHandler}. Two readings had to be discarded on the
 * way here, both measured against the production build in Firefox:
 *
 * - "did a navigation fail" is not enough, because an abandoned chunk is reported as a
 *   `NavigationError` just like a missing one;
 * - "did something succeed after it" is evaluated too early, because the rejection
 *   arrives while the navigation that replaced it is still loading its own chunk
 *   (measured: `NavigationError` id 1 with no `NavigationEnd` recorded yet).
 *
 * What holds is the navigation that was *started* last. Ids increase one per
 * navigation, so a failure carrying an older id belongs to a route the analyst already
 * steered away from.
 *
 * Constructed at startup by an app initializer, not on first use: an outcome that
 * happens before the first read still has to be seen.
 */
@Injectable({ providedIn: 'root' })
export class NavigationOutcomeService {
  private readonly router = inject(Router);

  private readonly lastStartIdSignal = signal(0);
  private readonly lastErrorId = signal(0);

  constructor() {
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.lastStartIdSignal.set(event.id);
        return;
      }
      if (event instanceof NavigationError) {
        this.lastErrorId.set(event.id);
      }
    });
  }

  /**
   * True when the navigation that failed is still the newest one - nothing else was
   * asked for since, so the analyst is stuck on the screen they were on instead of the
   * one they wanted. A failure carrying an older id was overtaken and no longer
   * describes what is on screen.
   */
  /** Id of the most recently started navigation, `0` before the first one. */
  lastStartId(): number {
    return this.lastStartIdSignal();
  }

  currentNavigationFailed(): boolean {
    return this.lastErrorId() > 0 && this.lastErrorId() >= this.lastStartIdSignal();
  }
}
